import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  HairlineCard,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import {
  pickLang,
  useLocalGovernmentWorkflows,
  useUpdateCitations,
} from '../../lib/bylaw-workflows-api';
import { useScope } from '../../providers/ScopeProvider';

/**
 * The municipality's by-law workflows, as its own people see them: which
 * by-law types its officers can open files under, the jurisdiction its
 * statutes are cited from, and the citations themselves.
 *
 * Which types run is a licensing decision made by Munifocus in the admin
 * portal, so it is shown, not edited. The citations are the municipality's
 * own — by-law numbers above all — and a holder of `workflow:configure` (the
 * clerk, by default) sets them here.
 */
export function BylawWorkflowsPanel() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { active, can } = useScope();
  const localGovernmentId = active?.kind === 'local_government' ? active.id : null;
  const workflows = useLocalGovernmentWorkflows(localGovernmentId);
  const update = useUpdateCitations(localGovernmentId ?? '');
  const editable = can('workflow:configure');

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const stamp = JSON.stringify(workflows.data?.statutes ?? []);
  useEffect(() => {
    setDrafts(
      Object.fromEntries((workflows.data?.statutes ?? []).map((s) => [s.key, s.override ?? ''])),
    );
  }, [stamp]);

  if (!localGovernmentId) {
    return (
      <HairlineCard sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('gov.bylawWorkflow.admin.needsMunicipality')}
        </Typography>
      </HairlineCard>
    );
  }
  if (workflows.isLoading) {
    return <CircularProgress aria-label={t('gov.bylawWorkflow.admin.loading')} />;
  }
  if (!workflows.data) {
    return <Alert severity="error">{t('gov.bylawWorkflow.admin.loadError')}</Alert>;
  }

  const data = workflows.data;
  const configured = data.available.filter((d) => d.engine === 'configured');

  return (
    <Stack spacing={2}>
      {!data.multiBylawEnabled && (
        <Alert severity="info">{t('gov.bylawWorkflow.admin.moduleOff')}</Alert>
      )}

      <HairlineCard sx={{ p: 2.5 }}>
        <Typography variant="overline">{t('gov.bylawWorkflow.admin.jurisdiction')}</Typography>
        <Typography variant="body1">{pickLang(data.jurisdiction.name, lang)}</Typography>
      </HairlineCard>

      <HairlineCard sx={{ p: 2.5 }}>
        <Typography variant="overline">{t('gov.bylawWorkflow.admin.types')}</Typography>
        <Stack spacing={1.25} divider={<Divider flexItem />} sx={{ mt: 1 }}>
          <Stack spacing={0.25}>
            <Typography variant="body2">{t('gov.bylaw.dangerous_unsightly')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('gov.bylawWorkflow.admin.originalWorkflow')}
            </Typography>
          </Stack>
          {configured.map((d) => (
            <Stack key={d.category} spacing={0.25}>
              <Typography variant="body2">{pickLang(d.label, lang)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {pickLang(d.summary, lang)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </HairlineCard>

      <HairlineCard sx={{ p: 2.5 }}>
        <Typography variant="overline">{t('gov.bylawWorkflow.admin.citations')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {editable
            ? t('gov.bylawWorkflow.admin.citationsHint')
            : t('gov.bylawWorkflow.admin.citationsReadOnly')}
        </Typography>
        <Stack spacing={2}>
          {data.statutes.map((s) => {
            const fallback = s.jurisdictionDefault || t('gov.bylawWorkflow.citationNotConfigured');
            return editable ? (
              <TextField
                key={s.key}
                size="small"
                fullWidth
                label={pickLang(s.label, lang)}
                value={drafts[s.key] ?? ''}
                placeholder={s.jurisdictionDefault}
                helperText={t('gov.bylawWorkflow.admin.defaultCitation', { citation: fallback })}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [s.key]: e.target.value }))}
                slotProps={{ htmlInput: { maxLength: 300 } }}
              />
            ) : (
              <Stack
                key={s.key}
                direction="row"
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <Typography variant="body2" color="text.secondary">
                  {pickLang(s.label, lang)}
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                  {s.override || fallback}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        {editable && (
          <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              disabled={update.isPending}
              onClick={() => update.mutate(drafts)}
            >
              {update.isPending
                ? t('gov.bylawWorkflow.stage.saving')
                : t('gov.bylawWorkflow.admin.saveCitations')}
            </Button>
          </Stack>
        )}
      </HairlineCard>
    </Stack>
  );
}
